<script lang="ts">
  import { onMount } from 'svelte';
  import { t } from '../lib/i18n';
  import type { OpenAIModel } from '../lib/types';
  import { openaiModelsApi } from '../lib/api';
  
  // Состояние компонента
  let models: OpenAIModel[] = [];
  let loading = false;
  let error = '';
  let success = '';
  let selectedModelId: string = '';
  
  // События
  import { createEventDispatcher } from 'svelte';
  const dispatch = createEventDispatcher<{
    modelSelected: { modelId: string };
  }>();
  
  // Загрузка моделей при монтировании компонента
  onMount(async () => {
    await loadModels();
  });
  
  // Загрузка моделей из API
  async function loadModels() {
    try {
      loading = true;
      error = '';
      
      // Получаем модели из API
      const apiModels = await openaiModelsApi.getAll(true);
      
      // Фильтруем created_at и updated_at поля из моделей
      models = apiModels.map(({ created_at, updated_at, ...rest }) => rest);
      
      // Найти дефолтную модель
      const defaultModel = models.find(model => model.is_default);
      if (defaultModel) {
        selectedModelId = defaultModel.model_id;
      } else if (models.length > 0) {
        selectedModelId = models[0].model_id;
      }
      
      loading = false;
    } catch (err) {
      error = err instanceof Error ? err.message : 'Не удалось загрузить модели OpenAI';
      loading = false;
    }
  }
  
  // Установка модели по умолчанию через API
  async function setDefaultModel() {
    if (!selectedModelId) return;
    
    try {
      loading = true;
      error = '';
      success = '';
      
      // Вызов API для установки модели по умолчанию
      const { created_at, updated_at, ...updatedModel } = await openaiModelsApi.setDefault(selectedModelId);
      
      // Обновляем статус is_default для всех моделей
      models = models.map(model => ({
        ...model,
        is_default: model.model_id === selectedModelId
      }));
      
      success = t('Модель по умолчанию успешно обновлена');
      setTimeout(() => {
        success = '';
      }, 3000);
      
      // Отправляем событие
      dispatch('modelSelected', { modelId: selectedModelId });
      
      loading = false;
    } catch (err) {
      error = err instanceof Error ? err.message : 'Не удалось установить модель по умолчанию';
      loading = false;
    }
  }
  
  // Форматирование стоимости для отображения
  function formatCost(cost?: number): string {
    if (cost === undefined || cost === null) return t('Н/Д');
    return `$${cost.toFixed(4)}`;
  }
  
  // Получение списка возможностей
  function getCapabilityBadges(model: OpenAIModel): string[] {
    const badges: string[] = [];
    const capabilities = model.capabilities || {};
    
    // Проверка соответствующих возможностей из API
    if (capabilities.text) badges.push('текст');
    if (capabilities.image) badges.push('изображения');
    if (capabilities.audio) badges.push('аудио');
    if (capabilities.vision) badges.push('зрение');
    if (capabilities.video) badges.push('видео');
    if (capabilities.code) badges.push('код');
    if (capabilities.reasoning) badges.push('рассуждения');
    if (capabilities.function_calling) badges.push('вызов функций');
    if (capabilities.multi_language) badges.push('многоязычность');
    if (capabilities.long_context) badges.push('длинный контекст');
    if (capabilities.advanced_reasoning) badges.push('продвинутые рассуждения');
    if (capabilities.step_by_step_logic) badges.push('пошаговая логика');
    if (capabilities.chain_of_thought) badges.push('цепочка мыслей');
    
    return badges;
  }
  
  // Обработка изменения модели
  function handleModelChange() {
    // Обновление модели по умолчанию при изменении выбора
    setDefaultModel();
  }
</script>

<div class="openai-model-selector">
  <h6 class="mb-3">{t('Управление моделями OpenAI')}</h6>
  
  {#if error}
    <div class="alert alert-danger alert-dismissible fade show" role="alert">
      {error}
      <button type="button" class="btn-close" on:click={() => error = ''} aria-label={t('Закрыть')}></button>
    </div>
  {/if}
  
  {#if success}
    <div class="alert alert-success alert-dismissible fade show" role="alert">
      {success}
      <button type="button" class="btn-close" on:click={() => success = ''} aria-label={t('Закрыть')}></button>
    </div>
  {/if}
  
  {#if loading}
    <div class="text-center py-3">
      <div class="spinner-border spinner-border-sm text-primary" role="status">
        <span class="visually-hidden">{t('Загрузка...')}</span>
      </div>
      <span class="ms-2">{t('Загрузка моделей...')}</span>
    </div>
  {:else if models.length === 0}
    <div class="alert alert-warning">
      {t('Нет доступных моделей OpenAI. Пожалуйста, проверьте конфигурацию.')}
    </div>
  {:else}
    <div class="mb-3">
      <label for="modelSelector" class="form-label">{t('Выберите модель по умолчанию')}</label>
      <select 
        id="modelSelector" 
        class="form-select"
        bind:value={selectedModelId}
        on:change={handleModelChange}
      >
        {#each models as model}
          <option value={model.model_id}>
            {model.name} {model.is_default ? `(${t('По умолчанию')})` : ''}
          </option>
        {/each}
      </select>
      <div class="form-text">{t('Модель, используемая для операций анализа AI')}</div>
    </div>
    
    <!-- Детали модели -->
    {#if selectedModelId}
      {#each models.filter(m => m.model_id === selectedModelId) as model}
        <div class="card mb-3">
          <div class="card-body">
            <h6 class="card-title d-flex justify-content-between align-items-center">
              <span>{model.name}</span>
              {#if model.is_default}
                <span class="badge bg-primary">{t('По умолчанию')}</span>
              {/if}
            </h6>
            
            <p class="card-text text-muted small">{model.description || t('Описание отсутствует')}</p>
            
            <div class="model-details">
              <!-- ID модели -->
              <div class="mb-2">
                <span class="fw-bold">{t('ID модели')}:</span>
                <span class="ms-2 text-monospace">{model.model_id}</span>
              </div>
              
              <!-- Стоимость -->
              <div class="mb-2">
                <span class="fw-bold">{t('Стоимость')}:</span>
                <span class="ms-2">{formatCost(model.cost_per_1k_tokens)} / 1K {t('токенов')}</span>
              </div>
              
              <!-- Лимиты токенов -->
              <div class="mb-2">
                <span class="fw-bold">{t('Лимит токенов')}:</span>
                <span class="ms-2">{model.token_limit?.toLocaleString() || t('Н/Д')}</span>
              </div>
              
              <!-- Максимальная длина запроса -->
              {#if model.max_tokens}
              <div class="mb-2">
                <span class="fw-bold">{t('Максимум токенов')}:</span>
                <span class="ms-2">{model.max_tokens.toLocaleString()}</span>
              </div>
              {/if}
              
              <!-- Возможности -->
              <div class="mb-2">
                <span class="fw-bold">{t('Возможности')}:</span>
                <div class="mt-1">
                  {#each getCapabilityBadges(model) as capability}
                    <span class="badge bg-secondary me-1 mb-1">{capability}</span>
                  {/each}
                </div>
              </div>
            </div>
          </div>
        </div>
      {/each}
    {/if}
  {/if}
</div>

<style>
  .openai-model-selector {
    font-size: 0.9rem;
  }
  
  .text-monospace {
    font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    font-size: 0.85rem;
  }
  
  .model-details {
    font-size: 0.85rem;
    line-height: 1.4;
  }
</style>